import { useState } from "react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Switch } from "../ui/switch";
import { Textarea } from "../ui/textarea";
import { Loader2, Plus, Check, ChevronsUpDown } from "lucide-react";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger,
} from "../ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../ui/select";
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from "../ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";

export function DishDialog({
  open, onOpenChange, editingDish, form, setForm,
  onSubmit, saving, categories, restaurantDishes,
}) {
  const [recipeComboOpen, setRecipeComboOpen] = useState(false);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <Button className="rounded-full" data-testid="add-dish-btn">
          <Plus className="w-4 h-4 mr-2" /> Plato
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{editingDish ? "Editar plato" : "Nuevo plato"}</DialogTitle>
          <DialogDescription>{editingDish ? "Modifica los datos del plato" : "Añade un nuevo plato al menu"}</DialogDescription>
        </DialogHeader>
        <form onSubmit={onSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label>Nombre *</Label>
            {editingDish ? (
              <div className="flex gap-2">
                <Input placeholder="Nombre del plato" value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })} className="flex-1" data-testid="dish-name-input" />
              </div>
            ) : (
              <Popover open={recipeComboOpen} onOpenChange={setRecipeComboOpen}>
                <PopoverTrigger asChild>
                  <Button variant="outline" role="combobox" className="w-full justify-between font-normal" data-testid="dish-recipe-selector">
                    {form.name || "Seleccionar receta existente..."} <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[400px] p-0" align="start">
                  <Command>
                    <CommandInput placeholder="Buscar receta..." data-testid="recipe-search-input" />
                    <CommandList>
                      <CommandEmpty>No se encontraron recetas.</CommandEmpty>
                      <CommandGroup heading="Recetas del restaurante">
                        {restaurantDishes.map((d) => (
                          <CommandItem key={d.dish_id} value={d.name}
                            onSelect={() => { setForm(prev => ({ ...prev, name: d.name, description: d.description || prev.description })); setRecipeComboOpen(false); }}>
                            <Check className={`mr-2 h-4 w-4 ${form.name === d.name ? "opacity-100" : "opacity-0"}`} />
                            <div className="flex items-center gap-2">
                              {d.image_url && <img src={d.image_url} alt={d.name} className="w-8 h-6 object-cover rounded" />}
                              <div>
                                <span className="font-medium">{d.name}</span>
                                {d.description && <span className="text-xs text-muted-foreground ml-2 line-clamp-1">{d.description}</span>}
                              </div>
                            </div>
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            )}
          </div>

          <div className="space-y-2">
            <Label>Descripcion</Label>
            <Textarea placeholder="Descripcion del plato..." value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Precio (EUR) *</Label>
              <Input type="number" step="0.01" placeholder="0.00" value={form.price}
                onChange={(e) => setForm({ ...form, price: e.target.value })} data-testid="dish-price-input" />
            </div>
            <div className="space-y-2">
              <Label>Categoria *</Label>
              <Select value={form.category_id} onValueChange={(v) => setForm({ ...form, category_id: v })}>
                <SelectTrigger data-testid="dish-category-select"><SelectValue placeholder="Seleccionar..." /></SelectTrigger>
                <SelectContent>
                  {categories.map((cat) => <SelectItem key={cat.category_id} value={cat.category_id}>{cat.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="flex items-center justify-between">
            <Label>Disponible</Label>
            <Switch checked={form.is_active} onCheckedChange={(v) => setForm({ ...form, is_active: v })} />
          </div>

          <div className="flex gap-3 justify-end">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
            <Button type="submit" disabled={saving} data-testid="save-dish-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : (editingDish ? "Guardar" : "Crear")}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
